import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [brokerages, setBrokerages] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    full_name: '', email: '', password: '', role: 'agent',
    brokerage_id: '', phone: '', license_number: '', buyer_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, brokRes, buyersRes] = await Promise.all([
        authFetch(`${API}/api/auth/users`),
        authFetch(`${API}/api/brokerages`),
        authFetch(`${API}/api/buyers`),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (brokRes.ok) setBrokerages(await brokRes.json());
      if (buyersRes.ok) setBuyers(await buyersRes.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');

    try {
      if (form.role === 'agent') {
        // Step 1: Create the agent record under the brokerage
        if (!form.brokerage_id) {
          setError('Please select a brokerage for this agent');
          setCreating(false);
          return;
        }

        const agentRes = await authFetch(`${API}/api/agents`, {
          method: 'POST',
          body: JSON.stringify({
            brokerage_id: parseInt(form.brokerage_id),
            name: form.full_name,
            email: form.email,
            phone: form.phone || null,
            license_number: form.license_number || null,
          })
        });

        if (!agentRes.ok) {
          const err = await agentRes.json();
          setError(err.detail || 'Failed to create agent profile');
          setCreating(false);
          return;
        }

        const agentData = await agentRes.json();

        // Step 2: Create the user account linked to the agent
        const userRes = await authFetch(`${API}/api/auth/register`, {
          method: 'POST',
          body: JSON.stringify({
            full_name: form.full_name,
            email: form.email,
            password: form.password,
            role: 'agent',
            brokerage_id: parseInt(form.brokerage_id),
            agent_id: agentData.id,
          })
        });

        if (userRes.ok) {
          setSuccess(`Agent "${form.full_name}" created under ${brokerages.find(b => b.id === parseInt(form.brokerage_id))?.name || 'brokerage'} with login access`);
          setForm({ full_name: '', email: '', password: '', role: 'agent', brokerage_id: '', phone: '', license_number: '', buyer_id: '' });
          setShowCreate(false);
          loadData();
        } else {
          const err = await userRes.json();
          setError(err.detail || 'Agent profile created but user account failed');
        }

      } else if (form.role === 'broker') {
        // Broker: just needs brokerage assignment
        if (!form.brokerage_id) {
          setError('Please select a brokerage for this broker');
          setCreating(false);
          return;
        }

        const res = await authFetch(`${API}/api/auth/register`, {
          method: 'POST',
          body: JSON.stringify({
            full_name: form.full_name,
            email: form.email,
            password: form.password,
            role: 'broker',
            brokerage_id: parseInt(form.brokerage_id),
          })
        });

        if (res.ok) {
          setSuccess(`Broker "${form.full_name}" created`);
          setForm({ full_name: '', email: '', password: '', role: 'agent', brokerage_id: '', phone: '', license_number: '', buyer_id: '' });
          setShowCreate(false);
          loadData();
        } else {
          const err = await res.json();
          setError(err.detail || 'Failed to create broker');
        }

      } else if (form.role === 'buyer') {
        if (!form.buyer_id) {
          setError('Please select a buyer to link this account to');
          setCreating(false);
          return;
        }

        const selectedBuyer = buyers.find(b => b.id === parseInt(form.buyer_id));
        const res = await authFetch(`${API}/api/auth/register`, {
          method: 'POST',
          body: JSON.stringify({
            full_name: form.full_name,
            email: form.email,
            password: form.password,
            role: 'buyer',
            brokerage_id: form.brokerage_id ? parseInt(form.brokerage_id) : null,
            agent_id: selectedBuyer?.agent_id || null,
            buyer_id: parseInt(form.buyer_id),
          })
        });

        if (res.ok) {
          setSuccess(`Buyer login "${form.full_name}" created`);
          setForm({ full_name: '', email: '', password: '', role: 'agent', brokerage_id: '', phone: '', license_number: '', buyer_id: '' });
          setShowCreate(false);
          loadData();
        } else {
          const err = await res.json();
          setError(err.detail || 'Failed to create buyer account');
        }

      } else if (form.role === 'super_admin') {
        const res = await authFetch(`${API}/api/auth/register`, {
          method: 'POST',
          body: JSON.stringify({
            full_name: form.full_name,
            email: form.email,
            password: form.password,
            role: 'super_admin',
          })
        });

        if (res.ok) {
          setSuccess(`Super Admin "${form.full_name}" created`);
          setForm({ full_name: '', email: '', password: '', role: 'agent', brokerage_id: '', phone: '', license_number: '', buyer_id: '' });
          setShowCreate(false);
          loadData();
        } else {
          const err = await res.json();
          setError(err.detail || 'Failed to create super admin');
        }
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await authFetch(`${API}/api/auth/users/${userId}/toggle-active`, { method: 'PUT' });
      if (res.ok) loadData();
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-purple-600 text-purple-100',
      broker: 'bg-blue-600 text-blue-100',
      agent: 'bg-green-600 text-green-100',
      buyer: 'bg-cyan-600 text-cyan-100',
      demo: 'bg-orange-600 text-orange-100',
    };
    const labels = {
      super_admin: 'Super Admin',
      broker: 'Broker',
      agent: 'Agent',
      buyer: 'Buyer',
      demo: 'Demo',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[role] || 'bg-gray-600 text-gray-100'}`}>
        {labels[role] || role}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">User Management</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">{users.length} user{users.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors w-full md:w-auto"
          >
            {showCreate ? 'Cancel' : '+ Add User'}
          </button>
        </div>

        {success && (
          <div className="bg-green-900/50 border border-green-600 text-green-200 px-4 py-3 rounded-lg mb-4 text-sm">{success}</div>
        )}
        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
        )}

        {/* Create Form */}
        {showCreate && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Add New User</h2>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Full Name *</label>
                <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="Emily Bohlin" required />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="emily@remax.com" required />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Password *</label>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="Min 8 characters" required />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Role *</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  {user?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                  <option value="broker">Broker</option>
                  <option value="agent">Agent</option>
                  <option value="buyer">Buyer</option>
                </select>
              </div>

              {/* Brokerage — shown for broker and agent roles */}
              {(form.role === 'broker' || form.role === 'agent') && (
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Brokerage *</label>
                  <select value={form.brokerage_id} onChange={(e) => setForm({ ...form, brokerage_id: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                    <option value="">Select brokerage...</option>
                    {brokerages.map(b => (
                      <option key={b.id} value={b.id}>{b.name} — {b.city}, {b.state}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Agent-specific fields */}
              {form.role === 'agent' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Phone</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                      placeholder="(321) 555-0123" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">License Number</label>
                    <input type="text" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                      className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                      placeholder="FL-RE-123456" />
                  </div>
                </>
              )}

              {/* Buyer-specific fields */}
              {form.role === 'buyer' && (
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Link to Buyer *</label>
                  <select value={form.buyer_id} onChange={(e) => {
                    const selectedBuyer = buyers.find(b => b.id === parseInt(e.target.value));
                    setForm({ ...form, buyer_id: e.target.value, full_name: form.full_name || selectedBuyer?.name || '' });
                  }}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                    <option value="">Select buyer...</option>
                    {buyers.map(b => (
                      <option key={b.id} value={b.id}>{b.name} — {b.email || 'no email'}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="md:col-span-2">
                <button type="submit" disabled={creating}
                  className="px-6 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors">
                  {creating ? 'Creating...' : 'Create Account'}
                </button>
                <p className="text-gray-500 text-xs mt-2">
                  {form.role === 'agent' ? 'This will create both the agent profile and login account in one step.' :
                   form.role === 'broker' ? 'Broker will have admin access to all agents in the selected brokerage.' :
                   form.role === 'buyer' ? 'Buyer will have read-only access to their match board and AI reports.' :
                   'Super Admin has full platform access across all brokerages.'}
                </p>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        {users.length === 0 ? (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
            <p className="text-[#B0BEC5] text-lg">No users found</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {users.map(u => (
                <div key={u.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white font-medium text-sm truncate">{u.full_name}</div>
                      <div className="text-gray-300 text-sm break-all">{u.email}</div>
                    </div>
                    <div className="shrink-0">{getRoleBadge(u.role)}</div>
                  </div>
                  <div className="mt-2 text-gray-400 text-xs">
                    {u.brokerage_name || '—'}
                    {u.agent_name && <span className="text-gray-500 ml-1">({u.agent_name})</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 items-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      u.is_active ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'
                    }`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {u.id !== user?.id && (
                      <button
                        onClick={() => handleToggleActive(u.id)}
                        className={`text-sm font-medium transition-colors ${
                          u.is_active ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'
                        }`}
                      >
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

          <div className="hidden md:block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#546E7A]/30">
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Email</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Role</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Brokerage</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Status</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors">
                      <td className="px-4 py-3 text-white font-medium text-sm">{u.full_name}</td>
                      <td className="px-4 py-3 text-gray-300 text-sm">{u.email}</td>
                      <td className="px-4 py-3 text-center">{getRoleBadge(u.role)}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {u.brokerage_name || '—'}
                        {u.agent_name && <span className="text-gray-500 text-xs ml-1">({u.agent_name})</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          u.is_active ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'
                        }`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.id !== user?.id && (
                          <button
                            onClick={() => handleToggleActive(u.id)}
                            className={`text-sm font-medium transition-colors ${
                              u.is_active ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'
                            }`}
                          >
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
